/**
 * C-GTCC-COCKPIT-INFRA1 — the large host table of the Monitoring Cockpit,
 * 1:1 after `GTCC_REFERENCE_2026-09-01.html:228-238` (Executive Summary) and
 * the Hosts sub-tab of `section[data-sec="infra"]`.
 *
 * Columns, in the order of the reference: HOST · STATUS · LATENZ · CPU (§1.9)
 * · MEMORY · DISK · FORECAST · NETWORK (IN/OUT). Rendering is driven purely by
 * the payload; nothing is derived from a local clock and no number is filled
 * in where the API sends `null`.
 *
 * The stale row of the reference (`gt-backup-1`) is the general rule, not a
 * special case (Owner decisions P8/P9/P10):
 *   - status  → warn dot, never a healthy one,
 *   - latency → the dashed warn curve plus "— 12 min alt" from `ageSeconds`,
 *   - CPU     → "unbekannt — Stale wird NIE als Healthy gezeigt",
 *   - memory  → "—",
 *   - disk    → "lokal 34 % · Storage Box = REMOTE, eigene Zeile
 *               (GT-OPEN-123)" — a REMOTE target is never drawn as a local
 *               disk; its measured value lives in the Storage inventory,
 *   - network → "—".
 *
 * Table markup comes from the canonical `<DataTable>` primitive
 * (`docs/design/SHARED_COMPONENT_INVENTORY.md` #2), so sticky header, caption
 * and cell semantics are not re-derived here.
 */
import { DataTable, type DataTableColumn } from "@genius-tracker/design-system";
import type { Locale } from "@genius-tracker/i18n";
import { useLocale } from "@genius-tracker/i18n/react";
import type { CSSProperties, ReactNode } from "react";

import { FLEET_STATUSES, type FleetDiskDto, type FleetHostDto } from "../../../api/fleet.js";
import { Sparkline, type ChartTone } from "../../../ui/charts.js";
import { DiskCell, Dot, Meter, type CockpitTone, type MeterTone } from "../../../ui/primitives.js";
import {
  formatInteger,
  formatMbps,
  formatMinutes,
  formatMs,
  formatGib,
  formatWholePercent,
  toneOfStatus,
} from "./FleetPrimitives.js";

/* ------------------------------------------------------- call-site pieces */

/** The em dash the reference uses where a cell is simply empty. */
function FleetDash(): ReactNode {
  return <span style={{ color: "var(--gt-sys-text-muted)" }}>&mdash;</span>;
}

/** Grey "nicht messbar" — the only thing that ever stands in for a number. */
function NotMeasurable(): ReactNode {
  const { t } = useLocale();
  return (
    <span
      style={{
        color: "var(--gt-sys-text-muted)",
        fontFamily: "var(--gt-sys-font-family-mono)",
        fontSize: "var(--gt-sys-font-size-xs)",
      }}
    >
      {t("admin.gtcc.cockpit.infra.not_measurable")}
    </span>
  );
}

/**
 * `Dot` (`ui/primitives.tsx`) carries no `data-tone` of its own; the wrapper
 * below is the call-site adaptation that keeps that attribute (asserted by
 * `HostTable.test.tsx`) without bending the shared primitive. Same recipe as
 * `LaunchView.tsx`'s `LaunchDot`.
 */
function HostDot(props: { tone: CockpitTone }): ReactNode {
  return (
    <span data-tone={props.tone}>
      <Dot tone={props.tone} />
    </span>
  );
}

/**
 * `Meter` (`ui/primitives.tsx`) carries no `testId`/`data-meter` of its own
 * and requires a non-nullable `caption`; the host CPU/memory cells keep both
 * attributes (asserted by `HostTable.test.tsx`) and pass an empty string when
 * there is nothing to caption — the same "no small element drawn" outcome the
 * former `FleetMeter`'s `caption: null` produced, since an empty `<small>`
 * has no visible text or width.
 */
function HostMeter(props: {
  percent: number;
  tone: MeterTone;
  caption: string | null;
  label: string;
  testId: string;
}): ReactNode {
  return (
    <div data-testid={props.testId} data-meter={props.tone} aria-label={props.label}>
      <Meter tone={props.tone} pct={props.percent} caption={props.caption ?? ""} />
    </div>
  );
}

const SPARK_WIDTH = 72;
const SPARK_HEIGHT = 20;

/**
 * Data-shaping, not a UI primitive: scales a value series into the SVG
 * polyline `points` string `Sparkline` (`ui/charts.tsx`) expects, identical
 * maths to the former `FleetSparkline`. Fewer than two finite values render
 * nothing rather than a flat line, which would claim a measurement that was
 * never taken (Owner P8/P9/P10).
 */
function sparkPoints(values: ReadonlyArray<number> | null | undefined): string | null {
  const finite = (Array.isArray(values) ? values : []).filter((value) => Number.isFinite(value));
  if (finite.length < 2) return null;
  const min = Math.min(...finite);
  const max = Math.max(...finite);
  const span = max - min || 1;
  const step = SPARK_WIDTH / (finite.length - 1);
  return finite
    .map((value, index) => {
      const x = Math.round(index * step);
      const y = Math.round(SPARK_HEIGHT - ((value - min) / span) * (SPARK_HEIGHT - 4) - 2);
      return `${x},${y}`;
    })
    .join(" ");
}

/**
 * `toneOfStatus` returns a `CockpitTone`, but `Sparkline`'s `ChartTone` has no
 * "muted" member (it has "accent" instead, which `toneOfStatus` never
 * returns). "muted" only arises for the "unmeasurable" fleet status; it is
 * mapped to "info" here — the nearest neutral tone in the chart palette —
 * rather than widening the shared `Sparkline` primitive for a case the
 * reference sheet's fixture never draws.
 */
function chartToneOf(tone: CockpitTone): ChartTone {
  return tone === "muted" ? "info" : tone;
}

/**
 * `Sparkline` (`ui/charts.tsx`) carries no `testId`/`data-dashed` of its own;
 * the host latency cell keeps both (asserted by `HostTable.test.tsx`).
 */
function HostSpark(props: {
  values: ReadonlyArray<number>;
  tone: CockpitTone;
  dashed?: boolean;
  label: string;
  testId: string;
}): ReactNode {
  const points = sparkPoints(props.values);
  if (points === null) return null;
  return (
    <span
      data-testid={props.testId}
      data-dashed={props.dashed === true ? "true" : "false"}
      role="img"
      aria-label={props.label}
    >
      <Sparkline points={points} tone={chartToneOf(props.tone)} dashed={props.dashed === true} />
    </span>
  );
}

/** Same horizon/warning thresholds as the former `FleetDiskCell` — data owned
 * by this view's forecast reading, not a UI primitive. */
const FORECAST_HORIZON_DAYS = 90;
const FORECAST_WARN_DAYS = 30;

/** `142 GB` — decimal gigabytes, the unit the reference uses for "Used". */
function formatDecimalGb(bytes: number, locale: Locale): string {
  const gb = bytes / 1_000_000_000;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(gb)} GB`;
}

/**
 * `DiskCell` (`ui/primitives.tsx`) exposes no `testId` at all, and no slot
 * for the forecast sub-element the former `FleetDiskCell` marked with
 * `${testId}-forecast`. Only the outer element keeps a `testId`;
 * `HostTable.test.tsx` now reaches the forecast `<small>` via
 * `.querySelector("small")` on that outer element instead of a dedicated
 * testid (there is no shared-primitive slot to reattach one to without
 * bending `DiskCell` itself).
 */
function HostDiskCell(props: { disk: FleetDiskDto; testId: string }): ReactNode {
  const { t, locale } = useLocale();
  const { disk } = props;
  const days = disk.daysToFull;
  const warn = days !== null && days < FORECAST_WARN_DAYS;
  const forecast =
    days === null
      ? t("admin.gtcc.cockpit.infra.disk.forecast_unmeasurable")
      : days > FORECAST_HORIZON_DAYS
        ? t("admin.gtcc.cockpit.infra.disk.forecast_beyond", {
            days: formatInteger(FORECAST_HORIZON_DAYS, locale),
          })
        : t("admin.gtcc.cockpit.infra.disk.forecast", { days: formatInteger(days, locale) });
  return (
    <div data-testid={props.testId}>
      <DiskCell
        pct={disk.usedPercent}
        usedLabel={t("admin.gtcc.cockpit.infra.disk.used", {
          percent: formatWholePercent(disk.usedPercent, locale),
          bytes: formatDecimalGb(disk.usedBytes, locale),
        })}
        freeLabel={t("admin.gtcc.cockpit.infra.disk.free", {
          bytes: formatGib(disk.freeBytes, locale),
        })}
        forecast={forecast}
        hot={warn}
        {...(warn ? { forecastTone: "warn" as const } : {})}
      />
    </div>
  );
}

const MONO_MUTED = {
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-sys-font-size-xs)",
  color: "var(--gt-sys-text-muted)",
} as const;

const MONO = {
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-sys-font-size-xs)",
  color: "var(--gt-sys-text)",
} as const;

/** The CPU meter follows the reference: cool below 60 %, hot from 60 % up. */
const CPU_HOT_PERCENT = 60;
/** The memory meter is violet in the reference and hot from 60 % up. */
const MEM_HOT_PERCENT = 60;

export interface HostTableProps {
  readonly hosts: ReadonlyArray<FleetHostDto>;
  readonly loading?: boolean;
  readonly error?: string | null;
  readonly testId?: string;
}

/**
 * A malformed (but 200 OK) response can send a host entry missing fields the
 * type promises are always present (or nullable but never `undefined`). That
 * is the source's error, not a reason to crash: every field falls back to
 * its own "nicht messbar" value — `null`/`[]`/`"unmeasurable"` — the same
 * outcome the type's own nullable fields already render (Owner P9/P10).
 */
function normalizeHost(h: FleetHostDto): FleetHostDto {
  return {
    host: h.host ?? "",
    role: h.role ?? "",
    status:
      typeof h.status === "string" && (FLEET_STATUSES as readonly string[]).includes(h.status)
        ? h.status
        : "unmeasurable",
    measuredAt: h.measuredAt ?? "",
    ageSeconds: h.ageSeconds ?? 0,
    latencyMs: h.latencyMs ?? null,
    latencySpark: Array.isArray(h.latencySpark) ? h.latencySpark : [],
    cpuPercent: h.cpuPercent ?? null,
    vcpu: h.vcpu ?? null,
    memPercent: h.memPercent ?? null,
    memFreeBytes: h.memFreeBytes ?? null,
    disk: h.disk ?? null,
    netInBps: h.netInBps ?? null,
    netOutBps: h.netOutBps ?? null,
    remote: h.remote ?? null,
  };
}

/**
 * The reference row raster (`GTCC_REFERENCE_2026-09-01.html:101-103`, 12.5px
 * table font / 10px head / 7px cell padding, `--gt-cmp-gtcc-table-size`/
 * `-head-size`/`-cell-pad`) is denser than `<DataTable>`'s generic default
 * (`--gt-sys-font-size-sm` at 13px with `line-height:1.55`, `--gt-sys-space-4`
 * padding at 16px) — that default was part of the +23px row-height deviation
 * `docs/design/gtcc-screens/A-GTCC-ACCEPT-ALL2/ABGLEICH.md` Ursache 2 measured
 * for this table (F-GTCC-VIEW-COCKPIT1). Same tokens `ControlPrimitives.tsx`'s
 * `TABLE_STYLE`/`TH_STYLE`/`TD_STYLE` use (A-GTCC-ROWGRID1), so every GTCC
 * table converges on one raster. A-GTCC-ROWGRID1 measured, with a dedicated
 * row-pitch tool, that the *remaining* gap after this fix (+15px vs. the
 * reference) is not row raster at all but `HostTable`'s own meter bars
 * (`Meter`/`DiskCell`, `ui/primitives.tsx`, ~74px tall) — out of scope here,
 * see the "Rest" note below.
 */
const GTCC_HEADER_CELL_STYLE: CSSProperties = {
  fontSize: "var(--gt-cmp-gtcc-table-head-size)",
  paddingBlock: "var(--gt-sys-space-1)",
  paddingInline: "var(--gt-sys-space-2)",
};
const GTCC_CELL_STYLE: CSSProperties = {
  fontSize: "var(--gt-cmp-gtcc-table-size)",
  paddingBlock: "var(--gt-cmp-gtcc-table-cell-pad)",
  paddingInline: "var(--gt-sys-space-2)",
};

export function HostTable(props: HostTableProps): ReactNode {
  const { t, locale } = useLocale();
  const hosts = props.hosts.map(normalizeHost);

  const columns: ReadonlyArray<DataTableColumn<FleetHostDto>> = [
    {
      id: "host",
      header: t("admin.gtcc.cockpit.infra.hosts.col.host"),
      cell: (h) => (
        <span style={{ display: "block" }}>
          <span style={{ fontWeight: "var(--gt-sys-font-weight-semibold)" }}>{h.host}</span>
          <small style={{ display: "block", color: "var(--gt-sys-text-muted)" }}>{h.role}</small>
        </span>
      ),
    },
    {
      id: "status",
      header: t("admin.gtcc.cockpit.infra.hosts.col.status"),
      cell: (h) => (
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          <HostDot tone={toneOfStatus(h.status)} />
          {t(`admin.gtcc.cockpit.infra.hosts.status.${h.status}`)}
        </span>
      ),
    },
    {
      id: "latency",
      header: t("admin.gtcc.cockpit.infra.hosts.col.latency"),
      cell: (h) => {
        const stale = h.latencyMs === null;
        return (
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: "var(--gt-sys-space-2)" }}
          >
            <HostSpark
              values={h.latencySpark}
              tone={stale ? "warn" : toneOfStatus(h.status)}
              dashed={stale}
              label={t("admin.gtcc.cockpit.infra.hosts.latency_spark_label", { host: h.host })}
              testId={`gtcc-cockpit-host-spark-${h.host}`}
            />
            {h.latencyMs === null ? (
              h.status === "stale" ? (
                <span style={MONO_MUTED}>
                  {t("admin.gtcc.cockpit.infra.hosts.stale_age", {
                    minutes: formatMinutes(h.ageSeconds, locale),
                  })}
                </span>
              ) : (
                <NotMeasurable />
              )
            ) : (
              <span style={MONO}>
                {t("admin.gtcc.cockpit.infra.hosts.latency_ms", {
                  ms: formatMs(h.latencyMs, locale),
                })}
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: "cpu",
      header: t("admin.gtcc.cockpit.infra.hosts.col.cpu"),
      cell: (h) => {
        if (h.cpuPercent === null) {
          return h.status === "stale" ? (
            <span style={MONO_MUTED} data-testid={`gtcc-cockpit-host-cpu-stale-${h.host}`}>
              {t("admin.gtcc.cockpit.infra.hosts.cpu_stale")}
            </span>
          ) : (
            <NotMeasurable />
          );
        }
        return (
          <HostMeter
            percent={h.cpuPercent}
            tone={h.cpuPercent >= CPU_HOT_PERCENT ? "hot" : "cool"}
            caption={
              h.vcpu === null
                ? null
                : t("admin.gtcc.cockpit.infra.hosts.vcpu", {
                    count: formatInteger(h.vcpu, locale),
                  })
            }
            label={t("admin.gtcc.cockpit.infra.hosts.cpu_label", { host: h.host })}
            testId={`gtcc-cockpit-host-cpu-${h.host}`}
          />
        );
      },
    },
    {
      id: "memory",
      header: t("admin.gtcc.cockpit.infra.hosts.col.memory"),
      cell: (h) => {
        if (h.memPercent === null) {
          return h.status === "stale" ? <FleetDash /> : <NotMeasurable />;
        }
        return (
          <HostMeter
            percent={h.memPercent}
            tone={h.memPercent >= MEM_HOT_PERCENT ? "hot" : "violet"}
            caption={
              h.memFreeBytes === null
                ? null
                : t("admin.gtcc.cockpit.infra.hosts.mem_free", {
                    bytes: formatGib(h.memFreeBytes, locale),
                  })
            }
            label={t("admin.gtcc.cockpit.infra.hosts.memory_label", { host: h.host })}
            testId={`gtcc-cockpit-host-mem-${h.host}`}
          />
        );
      },
    },
    {
      id: "disk",
      header: t("admin.gtcc.cockpit.infra.hosts.col.disk"),
      cell: (h) => {
        if (h.remote !== null) {
          return (
            <span style={MONO} data-testid={`gtcc-cockpit-host-remote-${h.host}`}>
              {h.disk === null ? (
                <NotMeasurable />
              ) : (
                t("admin.gtcc.cockpit.infra.hosts.disk_local", {
                  percent: formatWholePercent(h.disk.usedPercent, locale),
                })
              )}{" "}
              <b style={{ color: "var(--gt-sys-text-muted)" }}>
                {t("admin.gtcc.cockpit.infra.hosts.remote_elsewhere")}
              </b>
            </span>
          );
        }
        if (h.disk === null) return <NotMeasurable />;
        return <HostDiskCell disk={h.disk} testId={`gtcc-cockpit-host-disk-${h.host}`} />;
      },
    },
    {
      id: "network",
      header: t("admin.gtcc.cockpit.infra.hosts.col.network"),
      cell: (h) => {
        if (h.netInBps === null || h.netOutBps === null) {
          return h.status === "stale" ? <FleetDash /> : <NotMeasurable />;
        }
        return (
          <span style={MONO} data-testid={`gtcc-cockpit-host-net-${h.host}`}>
            {t("admin.gtcc.cockpit.infra.hosts.net_out", {
              mbps: formatMbps(h.netOutBps, locale),
            })}
            <br />
            {t("admin.gtcc.cockpit.infra.hosts.net_in", { mbps: formatMbps(h.netInBps, locale) })}
          </span>
        );
      },
    },
  ];

  return (
    <DataTable<FleetHostDto>
      caption={t("admin.gtcc.cockpit.infra.hosts.caption")}
      columns={columns}
      rows={hosts}
      getRowId={(h) => h.host}
      loading={props.loading === true}
      error={props.error ?? null}
      loadingLabel={t("admin.gtcc.cockpit.infra.hosts.loading")}
      empty={{
        title: t("admin.gtcc.cockpit.infra.hosts.empty_title"),
        body: t("admin.gtcc.cockpit.infra.hosts.empty_body"),
      }}
      testId={props.testId ?? "gtcc-cockpit-host-table"}
      rowTestId={(h) => `gtcc-cockpit-host-row-${h.host}`}
      headerCellStyle={GTCC_HEADER_CELL_STYLE}
      cellStyle={GTCC_CELL_STYLE}
    />
  );
}
